import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Contractor } from '../types/contractor';
import { BsThreeDotsVertical, BsBriefcase, BsClock, BsGeoAlt, BsStar, BsStarFill } from 'react-icons/bs';
import { MdOutlineVerified } from 'react-icons/md';
import { CreateDropdown } from '../components/CreateDropdown';
import { ValidateRole } from '../helpers/ValidateRole';
import { NewProjectForm } from '../components/NewProjectForm';
import { NotificationButton } from '../components/NotificationButton';
import { Avatar } from '../components/Avatar';

const CONTRACTORS: Contractor[] = [
  {
    id: '1',
    firstName: 'Michael',
    lastName: 'Chen',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    email: 'michael.chen@example.com',
    phone: '+1 (555) 123-4567',
    certificates: [
      { name: 'CDCP - Certified Data Centre Professional', issuer: 'CDCP Institute', validUntil: '2025-12-31' },
      { name: 'Electrical Safety Certification', issuer: 'Safety First', validUntil: '2024-06-30' }
    ],
    projects: [
      { id: 'p1', name: 'CloudTech DC Expansion', role: 'Lead Technician', duration: '6 months' },
      { id: 'p2', name: 'AWS Server Room Setup', role: 'Installation Specialist', duration: '3 months' }
    ],
    specialization: ['Power Distribution', 'Cooling Systems', 'Rack Installation'],
    tasks: [
      'Install and configure server racks',
      'Maintain cooling systems',
      'Perform electrical system upgrades',
      'Cable management and optimization'
    ],
    averageSalary: 85000,
    yearsOfExperience: 8,
    availability: 'available',
    location: 'San Jose, CA',
    skills: ['Electrical Systems', 'HVAC', 'Network Infrastructure'],
    preferredWorkHours: '8:00 AM - 5:00 PM',
    rating: 4.8
  },
  {
    id: '2',
    firstName: 'Sarah',
    lastName: 'Martinez',
    avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
    email: 'sarah.martinez@example.com',
    phone: '+1 (555) 234-5678',
    certificates: [
      { name: 'DCPRO Data Center Infrastructure Specialist', issuer: 'DCPRO', validUntil: '2024-12-31' },
      { name: 'Advanced Cooling Systems Expert', issuer: 'CoolMaster', validUntil: '2025-03-31' }
    ],
    projects: [
      { id: 'p3', name: 'CloudServ Data Center', role: 'Cooling Systems Expert', duration: '8 months' },
      { id: 'p4', name: 'DataFlow DC Upgrade', role: 'Senior Technician', duration: '4 months' }
    ],
    specialization: ['HVAC Systems', 'Environmental Monitoring', 'Energy Efficiency'],
    tasks: [
      'Design cooling solutions',
      'Optimize energy consumption',
      'Monitor environmental conditions',
      'Implement green solutions'
    ],
    averageSalary: 92000,
    yearsOfExperience: 10,
    availability: 'on_project',
    location: 'Austin, TX',
    skills: ['HVAC Design', 'Energy Management', 'Environmental Control'],
    preferredWorkHours: '9:00 AM - 6:00 PM',
    rating: 4.5
  },
  {
    id: '3',
    firstName: 'David',
    lastName: 'Wilson',
    avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
    email: 'david.wilson@example.com',
    phone: '+1 (555) 345-6789',
    certificates: [
      { name: 'Network Infrastructure Professional', issuer: 'NetPro', validUntil: '2025-06-30' },
      { name: 'Fiber Optics Specialist', issuer: 'FOA', validUntil: '2024-09-30' }
    ],
    projects: [
      { id: 'p5', name: 'Oracle Cloud Infrastructure', role: 'Network Specialist', duration: '5 months' },
      { id: 'p6', name: 'IBM Data Center', role: 'Cabling Expert', duration: '7 months' }
    ],
    specialization: ['Network Cabling', 'Fiber Optics', 'Infrastructure Planning'],
    tasks: [
      'Install fiber optic cables',
      'Configure network infrastructure',
      'Perform cable testing',
      'Document network layouts'
    ],
    averageSalary: 88000,
    yearsOfExperience: 7,
    availability: 'available',
    location: 'Seattle, WA',
    skills: ['Network Cabling', 'Fiber Optics', 'Network Infrastructure'],
    preferredWorkHours: '9:00 AM - 6:00 PM',
    rating: 4.2
  },
  {
    id: '4',
    firstName: 'Emily',
    lastName: 'Johnson',
    avatar: 'https://randomuser.me/api/portraits/women/4.jpg',
    email: 'emily.johnson@example.com',
    phone: '+1 (555) 456-7890',
    certificates: [
      { name: 'Power Systems Expert', issuer: 'PowerPro', validUntil: '2025-08-31' },
      { name: 'UPS Maintenance Specialist', issuer: 'UPS Corp', validUntil: '2024-11-30' }
    ],
    projects: [
      { id: 'p7', name: 'Digital Ocean DC', role: 'Power Systems Lead', duration: '9 months' },
      { id: 'p8', name: 'Rackspace Facility', role: 'UPS Specialist', duration: '4 months' }
    ],
    specialization: ['Power Systems', 'UPS Maintenance', 'Emergency Power'],
    tasks: [
      'Maintain UPS systems',
      'Install power distribution units',
      'Perform electrical maintenance',
      'Emergency power testing'
    ],
    averageSalary: 90000,
    yearsOfExperience: 9,
    availability: 'vacation',
    location: 'Denver, CO',
    skills: ['UPS Systems', 'Electrical Distribution', 'Emergency Power'],
    preferredWorkHours: '8:00 AM - 5:00 PM',
    rating: 4.7
  },
  {
    id: '5',
    firstName: 'James',
    lastName: 'Taylor',
    avatar: 'https://randomuser.me/api/portraits/men/5.jpg',
    email: 'james.taylor@example.com',
    phone: '+1 (555) 567-8901',
    certificates: [
      { name: 'Security Systems Professional', issuer: 'SecurePro', validUntil: '2025-04-30' },
      { name: 'Access Control Specialist', issuer: 'AccessTech', validUntil: '2024-07-31' }
    ],
    projects: [
      { id: 'p9', name: 'Equinix Data Center', role: 'Security Lead', duration: '6 months' },
      { id: 'p10', name: 'CyrusOne Facility', role: 'Access Control Expert', duration: '5 months' }
    ],
    specialization: ['Physical Security', 'Access Control', 'Surveillance Systems'],
    tasks: [
      'Install security systems',
      'Configure access control',
      'Maintain surveillance equipment',
      'Security audits'
    ],
    averageSalary: 87000,
    yearsOfExperience: 6,
    availability: 'available',
    location: 'Phoenix, AZ',
    skills: ['Security Systems', 'Access Control', 'CCTV'],
    preferredWorkHours: '7:30 AM - 4:30 PM',
    rating: 4.3
  }
];

const getAvailabilityColor = (availability: Contractor['availability']) => {
  switch (availability) {
    case 'available':
      return 'bg-green-100 text-green-800';
    case 'on_project':
      return 'bg-primary/10 text-primary';
    case 'vacation':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getAvailabilityLabel = (availability: Contractor['availability']) => {
  switch (availability) {
    case 'available':
      return 'Available';
    case 'on_project':
      return 'On Project';
    case 'vacation':
      return 'On Vacation';
    default:
      return 'Unknown';
  }
};

const StarRating = ({ rating }: { rating: number }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - Math.ceil(rating);

  const getRatingText = (rating: number) => {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 4.0) return 'Very Good';
    if (rating >= 3.5) return 'Good';
    if (rating >= 3.0) return 'Average';
    return 'Below Average';
  };

  return (
    <div className="flex items-center gap-0.5 group relative">
      <div className="flex items-center gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <BsStarFill key={`full-${i}`} className="w-4 h-4 text-yellow-400" />
        ))}
        {hasHalfStar && <BsStarFill className="w-4 h-4 text-yellow-400" />}
        {[...Array(emptyStars)].map((_, i) => (
          <BsStar key={`empty-${i}`} className="w-4 h-4 text-gray-300" />
        ))}
      </div>
      <span className="ml-1 text-sm text-gray-600">{rating.toFixed(1)}</span>
      <div className="absolute -top-8 left-0 bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
        {getRatingText(rating)}
      </div>
    </div>
  );
};

export const Contractors = () => {
  const navigate = useNavigate();
  const [expandedContractor, setExpandedContractor] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState<boolean>(false);

  return (
    <div className="p-6">
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Data Center Contractors</h1>
        <div className="flex items-center gap-4">
          <ValidateRole userRoles={['agency-admin']} expectedRoles={['agency-admin']}>
            <CreateDropdown onSelectProject={() => setShowNewProject(true)} />
          </ValidateRole>
          <NotificationButton />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {CONTRACTORS.map((contractor) => (
          <div key={contractor.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <Avatar
                    firstName={contractor.firstName}
                    lastName={contractor.lastName}
                    email={contractor.email}
                    size={48}
                    src={contractor.avatar}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">
                        {contractor.firstName} {contractor.lastName}
                      </h3>
                      <StarRating rating={contractor.rating} />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAvailabilityColor(contractor.availability)}`}>
                        {getAvailabilityLabel(contractor.availability)}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <BsGeoAlt className="w-4 h-4" />
                        {contractor.location}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <BsBriefcase className="w-4 h-4" />
                        {contractor.yearsOfExperience} years exp.
                      </span>
                    </div>
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700">Current Projects:</div>
                      <div className="mt-1 space-y-1">
                        {contractor.projects.map((project) => (
                          <div key={project.id} className="text-sm text-gray-600 flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary"></div>
                            <span className="font-medium">{project.name}</span>
                            <span className="text-gray-400">•</span>
                            <span>{project.role}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500">{project.duration}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedContractor(expandedContractor === contractor.id ? null : contractor.id)}
                  className="text-gray-400 hover:text-gray-600 ml-4"
                >
                  <BsThreeDotsVertical className="w-5 h-5" />
                </button>
              </div>

              {expandedContractor === contractor.id && (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-2">Contact Information</h4>
                      <p className="text-sm text-gray-600">{contractor.email}</p>
                      <p className="text-sm text-gray-600">{contractor.phone}</p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Work Schedule</h4>
                      <p className="text-sm text-gray-600 flex items-center gap-2">
                        <BsClock className="w-4 h-4" />
                        {contractor.preferredWorkHours}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Certificates</h4>
                      <div className="space-y-2">
                        {contractor.certificates.map((cert, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <MdOutlineVerified className="w-5 h-5 text-green-500 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium">{cert.name}</p>
                              <p className="text-xs text-gray-500">Valid until {new Date(cert.validUntil).toLocaleDateString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Recent Projects</h4>
                      <div className="space-y-2">
                        {contractor.projects.map((project) => (
                          <div key={project.id} className="text-sm">
                            <p className="font-medium">{project.name}</p>
                            <p className="text-gray-600">{project.role} • {project.duration}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="font-medium mb-2">Specialization & Tasks</h4>
                    <div className="flex flex-wrap gap-2">
                      {contractor.specialization.map((spec, index) => (
                        <span key={index} className="bg-primary/10 text-primary px-2 py-1 rounded-full text-sm">
                          {spec}
                        </span>
                      ))}
                    </div>
                    <ul className="mt-3 space-y-1">
                      {contractor.tasks.map((task, index) => (
                        <li key={index} className="text-sm text-gray-600">• {task}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-6">
                    <h4 className="font-medium mb-2">Skills</h4>
                    <div className="flex flex-wrap gap-2">
                      {contractor.skills.map((skill, index) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="font-medium">Average Salary</h4>
                    <p className="text-2xl font-semibold text-gray-900">
                      ${contractor.averageSalary.toLocaleString()}/year
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 